/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Divider, Typography } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import StyledOrderHistory from "./StyledOrderHistory";
import OrderHistoryCard from "./OrderHistoryCard";
import { fetchOrderHistory } from "../../store/reducers/orderHistory/apiThunk";
import { useDispatch } from "react-redux";
import { DateRangePicker } from "rsuite";
import "rsuite/dist/rsuite.min.css";
import "./datePickerStyles.css";
import moment from "moment";
import FilterListOutlinedIcon from "@mui/icons-material/FilterListOutlined";
import FilterModal from "./FilterModal";
import { useNavigate } from "react-router-dom";
import { CustomizedShimmer } from "../../components/CommonComponents/Shimmer";
import { ROUTE_PATH } from "../../constants/route";

const orderStatus = [
  "ALL",
  "COMPLETED",
  "RETURNED",
  "READY FOR PICKUP",
  "PROCESSING",
  "DELIVERED TO HOTEL",
  "CANCELLED",
  "IN TRANSIT TO HOTEL",
  "IN TRANSIT TO WANDEROBE",
];
const OrderHistory = () => {
  const [selectedDateRange, setSelectedDateRange] = useState(null);
  const [formattedDateRange, setFormattedDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [selectedStatuses, setSelectedStatuses] = useState(["ALL"]);
  const [newData, setNewData] = useState([]);
  const [pageDetails, setPageDetails] = useState(null);
  const [openFilterModal, setOpenFilterModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const loadMoreRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  useEffect(() => {
    let timeoutId = null;
  
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          pageDetails?.total_pages > currentPage &&
          !loading
        ) {
          timeoutId = setTimeout(() => {
            setCurrentPage((prev) => prev + 1);
          }, 500);
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );
  
    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }
  
    return () => {
      if (loadMoreRef.current) {
        observer.unobserve(loadMoreRef.current);
      }
      clearTimeout(timeoutId);
    };
  }, [pageDetails, currentPage, loading]);

  useEffect(() => {
    if (selectedDateRange?.[0] && selectedDateRange?.[1]) {
      setFormattedDateRange({
        startDate: moment(selectedDateRange[0]).format("YYYY-MM-DD"),
        endDate: moment(selectedDateRange[1]).format("YYYY-MM-DD"),
      });
    } else {
      setFormattedDateRange({ startDate: null, endDate: null });
    }
  }, [selectedDateRange]);

  const getOrderHistoryData = async () => {
    setLoading(true);
    try {
      const params = `?start_date=${formattedDateRange.startDate || ""}&end_date=${formattedDateRange.endDate || ""}&status=${selectedStatuses.includes("ALL") ? "" : selectedStatuses.join(",").replace(/\s+/g, '_')}&page_no=${currentPage}&page_size=10`;
      const res = await dispatch(fetchOrderHistory(params));
      const { status, data, _meta } = res?.payload;
      if (status) {
        if (currentPage > 1) {
          setNewData([...newData, ...data]);
        } else {
          setNewData(data);
        }
        setPageDetails(_meta);
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching trip details:", error);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrderHistoryData();
  }, [currentPage]);

  const handleStatusClick = (status) => {
    if (status === "ALL") {
      setSelectedStatuses(["ALL"]);
    } else {
      setSelectedStatuses((prev) => {
        const updated = prev.includes(status)
          ? prev.filter((s) => s !== status)
          : [...prev.filter((s) => s !== "ALL"), status];

        return updated.length === 0 ? ["ALL"] : updated;
      });
    }
  };
  const clearAll = () => {
    setLoading(true);
    setSelectedDateRange(null);
    setSelectedStatuses(["ALL"]);
    setCurrentPage(1);

    const emptyParams = `?start_date=&end_date=&status=&page_no=1&page_size=10`;
    dispatch(fetchOrderHistory(emptyParams))
      .then((res) => {
        const { status, data, _meta } = res?.payload;
        if (status) {
          setNewData(data);
          setPageDetails(_meta);
          setLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching order history:", error);
        setLoading(false);
      });
  };

  return (
    <StyledOrderHistory>
      {loading ? (
        <Box className='loader-container'>
          <CustomizedShimmer />
        </Box>
      ) : (
        <Box className='order-history-container-wrap'>
          <Box className='order-history-header'>
            <Box className='back-btn-order-header'>
              <ArrowBackIcon
                sx={{ fontSize: "20px", cursor: "pointer" }}
                onClick={() => {
                  navigate(ROUTE_PATH.USER_PROFILE);
                }}
              />
              <Typography className='order-history-title'>
                Order History
              </Typography>
            </Box>
            <Button
              variant='outlined'
              className='filter-btn'
              onClick={() => setOpenFilterModal(true)}
            >
              <FilterListOutlinedIcon />
              <Typography>Filter</Typography>
            </Button>
          </Box>

          <Box className='order-history-container'>
            <Box className='order-history-filter'>
              <Typography className='filter-title'>Filter</Typography>

              <Box className='filter-by-date-range'>
                <Typography className='filter-by-date-range-heading'>
                  Filter by Date range
                </Typography>
                <Box
                  className='date-picker-container'
                  sx={{ position: "relative", marginTop: "10px" }}
                >
                  <DateRangePicker
                    value={selectedDateRange}
                    onChange={(value) => setSelectedDateRange(value)}
                    placeholder='Select date range'
                    className='date-range-picker'
                    format='MM/dd/yyyy'
                    cleanable
                    block
                    renderValue={(value, placeholder) => {
                      if (
                        !value ||
                        value.length !== 2 ||
                        !value[0] ||
                        !value[1]
                      ) {
                        return placeholder || "Select date range";
                      }

                      return `${moment(value[0]).format("D MMMM YYYY")} - ${moment(value[1]).format("D MMMM YYYY")}`;
                    }}
                  />
                </Box>
              </Box>

              <Divider sx={{ margin: "20px 0px" }} />
              <Box>
                <Typography className='filter-status-header'>
                  Filter by Order status
                </Typography>
                <Box className='filter-by-order-status'>
                  {orderStatus.map((element) => {
                    const isSelected = selectedStatuses.includes(element);

                    return (
                      <Box
                        key={element}
                        className={`order-status-tag ${isSelected ? "selected" : ""}`}
                        onClick={() => handleStatusClick(element)}
                        sx={{ cursor: "pointer" }}
                      >
                        <Typography className='order-status-tag-text'>
                          {element.charAt(0).toUpperCase() +
                            element.slice(1).toLowerCase()}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
              <Divider sx={{ margin: "20px 0px" }} />
              <Box className='filter-btns'>
                <Button
                  variant='outlined'
                  className='cancel-all-btn'
                  onClick={() => {
                    clearAll();
                  }}
                >
                  Clear all
                </Button>
                <Button
                  variant='contained'
                  className='apply-filter-btn'
                  onClick={() => {
                    setCurrentPage(1);
                    getOrderHistoryData();
                  }}
                >
                  Apply filter
                </Button>
              </Box>
            </Box>

            <Box className='order-history-orders'>
              <Typography className='total-orders-heading'>
                {`${pageDetails?.total_count || 0} total ${pageDetails?.total_count > 1 ? "orders" : "order"}`}
              </Typography>
              <OrderHistoryCard data={newData} />
              <Box className='load-more-btn-wrap'>
                {pageDetails?.total_pages > currentPage && (
                  <Button
                    variant='outlined'
                    className='load-more-btn'
                    onClick={() => setCurrentPage(currentPage + 1)}
                    ref={loadMoreRef}
                  >
                    Load More
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      <FilterModal
        open={openFilterModal}
        onClose={() => {
          setOpenFilterModal(false);
          getOrderHistoryData();
        }}
        selectedStatuses={selectedStatuses}
        handleStatusClick={handleStatusClick}
        selectedDateRange={selectedDateRange}
        setSelectedDateRange={setSelectedDateRange}
        setSelectedStatuses={setSelectedStatuses}
      />
    </StyledOrderHistory>
  );
};

export default OrderHistory;
